import { useState, useEffect, useCallback } from "react";
import { ContentLayout } from "@/components/layout/ContentLayout";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Pencil, Trash2, MoveUp, MoveDown } from "lucide-react";
import { Link } from "react-router-dom";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/types/supabase";
import * as lucideIcons from "lucide-react";

type ExpertiseArea = Database['public']['Tables']['expertise_areas']['Row'];

export default function ExpertiseAreas() {
  const [expertise, setExpertise] = useState<ExpertiseArea[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  const fetchExpertise = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('expertise_areas')
        .select()
        .order('sort_order');

      if (error) throw error;

      setExpertise(data || []);
    } catch (error) {
      console.error('Error fetching expertise areas:', error);
      toast({
        title: "Hata",
        description: "Uzmanlık alanları yüklenirken bir sorun oluştu",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchExpertise();
  }, [fetchExpertise]);

  const deleteExpertise = async (id: string) => {
    try {
      const { error } = await supabase
        .from('expertise_areas')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Başarılı",
        description: "Uzmanlık alanı başarıyla silindi",
      });

      fetchExpertise();
    } catch (error) {
      console.error('Error deleting expertise area:', error);
      toast({
        title: "Hata",
        description: "Uzmanlık alanı silinirken bir sorun oluştu",
        variant: "destructive",
      });
    }
  };

  const moveExpertise = async (id: string, direction: 'up' | 'down') => {
    const currentIndex = expertise.findIndex(item => item.id === id);
    if (
      (direction === 'up' && currentIndex === 0) ||
      (direction === 'down' && currentIndex === expertise.length - 1)
    ) {
      return;
    }

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    const currentItem = expertise[currentIndex];
    const newItem = expertise[newIndex];

    try {
      const { error: error1 } = await supabase
        .from('expertise_areas')
        .update({ sort_order: currentItem.sort_order })
        .eq('id', newItem.id);

      if (error1) throw error1;

      const { error: error2 } = await supabase
        .from('expertise_areas')
        .update({ sort_order: newItem.sort_order })
        .eq('id', currentItem.id);

      if (error2) throw error2;

      fetchExpertise();
    } catch (error) {
      console.error('Error moving expertise area:', error);
      toast({
        title: "Hata",
        description: "Uzmanlık alanı taşınırken bir sorun oluştu",
        variant: "destructive",
      });
    }
  };

  const getLocalizedContent = (content: string) => {
    try {
      const parsed = JSON.parse(content);
      return parsed.tr || content;
    } catch {
      return content;
    }
  };

  if (isLoading) {
    return (
      <ContentLayout title="Uzmanlık Alanlarımız">
        <div className="flex items-center justify-center p-6">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
        </div>
      </ContentLayout>
    );
  }

  return (
    <ContentLayout 
      title="Uzmanlık Alanlarımız"
      actionButton={{
        label: "Yeni Uzmanlık Alanı",
        href: "/content/about/expertise/new"
      }}
    >
      <ScrollArea className="h-[calc(100vh-13rem)]">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Başlık</TableHead>
              <TableHead>Açıklama</TableHead>
              <TableHead>İkon</TableHead>
              <TableHead>İşlemler</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {expertise.map((item) => (
              <TableRow key={item.id}>
                <TableCell className="font-medium">{getLocalizedContent(item.title)}</TableCell>
                <TableCell className="max-w-[400px] truncate">{getLocalizedContent(item.description)}</TableCell>
                <TableCell>
                  {item.icon && (() => {
                    const Icon = lucideIcons[item.icon];
                    return <Icon className="h-5 w-5" />;
                  })()}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="icon" onClick={() => moveExpertise(item.id, "up")}>
                      <MoveUp className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => moveExpertise(item.id, "down")}>
                      <MoveDown className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" asChild>
                      <Link to={`/content/about/expertise/${item.id}/edit`}>
                        <Pencil className="h-4 w-4" />
                      </Link>
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => deleteExpertise(item.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ScrollArea>
    </ContentLayout>
  );
}
